export type InstagramMetricDirection = "up" | "down" | "flat";

export interface InstagramSummaryMetric {
  id: string;
  title: string;
  value: number;
  unit?: string;
  change: number;
  direction: InstagramMetricDirection;
  description: string;
  icon: string;
}

export interface InstagramTrendPoint {
  label: string;
  reach: number;
  impressions: number;
  interactions: number;
  profileActivity: number;
}

/** Puntos diarios (o casi) solo para el mini gráfico de balance de seguidores. */
export interface FollowerBalanceDay {
  label: string;
  net: number;
}

export interface InstagramAudienceSegment {
  label: string;
  value: number;
  percentage: number;
}

export interface InstagramStatItem {
  id: string;
  title: string;
  value: number;
  unit?: string;
  /** Variación porcentual del periodo (solo UI / mock hasta integrar API). */
  change?: number;
  direction?: InstagramMetricDirection;
  description?: string;
}

export interface InstagramTopContent {
  id: string;
  type: "Reel" | "Carrusel" | "Historia" | "Post";
  title: string;
  date: string;
  time: string;
  reach: number;
  views: number;
  interactions: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  engagementRate: number;
  status: "Nuevo" | "Publicado";
}

export interface InstagramHashtagRow {
  id: string;
  hashtag: string;
  posts: number;
  reach: number;
  interactions: number;
  engagementRate: number;
}

export interface InstagramAnalyticsSnapshot {
  accountName: string;
  communityName: string;
  updatedAt: string;
  periodLabel: string;
  summary: InstagramSummaryMetric[];
  growth: InstagramStatItem[];
  followerBalance: InstagramStatItem[];
  organicSummary: InstagramStatItem[];
  organicInteractions: InstagramStatItem[];
  trend: InstagramTrendPoint[];
  followerBalanceDaily: FollowerBalanceDay[];
  gender: InstagramAudienceSegment[];
  age: InstagramAudienceSegment[];
  countries: InstagramAudienceSegment[];
  cities: InstagramAudienceSegment[];
  ageGender: InstagramAudienceSegment[];
  topContent: InstagramTopContent[];
  hashtags: InstagramHashtagRow[];
}

// Front-only contract for the future BE integration.
// The BE can return this shape directly or the frontend can map an API response into it.
export const instagramAnalyticsMock: InstagramAnalyticsSnapshot = {
  accountName: "@mimundoenmovimiento",
  communityName: "mimundoenmovimiento",
  updatedAt: "2026-05-06T09:20:00-04:00",
  periodLabel: "6 abr 2026 - 5 may 2026",
  summary: [
    {
      id: "reach",
      title: "Alcance",
      value: 7188,
      change: -12.6,
      direction: "down",
      description: "Alcance medio diario: 52",
      icon: "bullseye",
    },
    {
      id: "impressions",
      title: "Visualizaciones",
      value: 7188,
      change: -8.2,
      direction: "down",
      description: "Visualizaciones totales del contenido",
      icon: "eye",
    },
    {
      id: "engagement",
      title: "Cuentas con interacción",
      value: 68,
      change: -4.7,
      direction: "down",
      description: "Interacciones orgánicas en el periodo",
      icon: "heart",
    },
    {
      id: "content",
      title: "Contenido total",
      value: 8,
      change: 16,
      direction: "up",
      description: "Publicaciones en el periodo",
      icon: "images",
    },
  ],
  growth: [
    {
      id: "followers",
      title: "Seguidores",
      value: 498,
      change: -0.7,
      direction: "down",
      description: "Variación del periodo",
    },
    {
      id: "following",
      title: "Siguiendo",
      value: 30,
      change: 2.1,
      direction: "up",
      description: "Cuentas seguidas desde el perfil",
    },
    {
      id: "total-content",
      title: "Contenido total",
      value: 8,
      change: 11.8,
      direction: "up",
      description: "Publicaciones en el periodo",
    },
    {
      id: "new-followers",
      title: "Nuevos seguidores",
      value: 16,
      change: 8.4,
      direction: "up",
      description: "Altas en el periodo",
    },
  ],
  followerBalance: [
    {
      id: "daily-followers",
      title: "Seguidores diarios",
      value: 0.53,
      description: "Promedio del periodo",
    },
    {
      id: "followers-post",
      title: "Seguidores por publicación",
      value: 2,
      description: "Promedio por post",
    },
    {
      id: "following-balance",
      title: "Siguiendo",
      value: 0,
      description: "Balance neto",
    },
    {
      id: "posts-day",
      title: "Publicaciones por día",
      value: 0.27,
      description: "Frecuencia diaria",
    },
    {
      id: "posts-week",
      title: "Publicaciones por semana",
      value: 1.87,
      description: "Frecuencia semanal",
    },
  ],
  organicSummary: [
    {
      id: "engagement",
      title: "Engagement",
      value: 3.69,
      unit: "%",
      direction: "down",
    },
    {
      id: "interactions",
      title: "Interacciones",
      value: 55,
      direction: "down",
    },
    {
      id: "avg-reach-post",
      title: "Promedio alcance/post",
      value: 186,
      direction: "down",
    },
    {
      id: "views",
      title: "Visualizaciones",
      value: 3080,
      direction: "up",
    },
    {
      id: "posts",
      title: "Posts",
      value: 8,
      direction: "up",
    },
  ],
  organicInteractions: [
    {
      id: "likes",
      title: "Me gusta",
      value: 51,
      direction: "down",
    },
    {
      id: "comments",
      title: "Comentarios",
      value: 2,
      direction: "down",
    },
    {
      id: "saved",
      title: "Guardado",
      value: 2,
      direction: "flat",
    },
    {
      id: "posts",
      title: "Posts",
      value: 8,
      direction: "up",
    },
    {
      id: "likes-day",
      title: "Me gusta diarios",
      value: 1.7,
    },
    {
      id: "likes-post",
      title: "Me gusta por publicación",
      value: 6.38,
    },
    {
      id: "comments-day",
      title: "Comentarios diarios",
      value: 0.07,
    },
    {
      id: "comments-post",
      title: "Comentarios por publicación",
      value: 0.25,
    },
    {
      id: "likes-comment",
      title: "Me gusta por comentario",
      value: 25.5,
    },
  ],
  trend: [
    { label: "6 abr", reach: 553, impressions: 1140, interactions: 48, profileActivity: 23 },
    { label: "13 abr", reach: 312, impressions: 680, interactions: 22, profileActivity: 12 },
    { label: "21 abr", reach: 245, impressions: 473, interactions: 7, profileActivity: 5 },
    { label: "28 abr", reach: 130, impressions: 241, interactions: 1, profileActivity: 1 },
    { label: "30 abr", reach: 62, impressions: 109, interactions: 2, profileActivity: 2 },
    { label: "2 may", reach: 194, impressions: 314, interactions: 8, profileActivity: 4 },
    { label: "5 may", reach: 71, impressions: 123, interactions: 4, profileActivity: 3 },
  ],
  followerBalanceDaily: (() => {
    const aprilNets = [
      5, 2, 4, -1, 3, 6, 2, 5, 1, -2, 4, 3, 8, 2, 3, -1, 5, 4, 1, 2, 6, 0, 3, -1, 4,
    ];
    const mayNets = [2, 3, 5, 2, 1];
    return [
      ...aprilNets.map((net, i) => ({ label: `${i + 6} abr`, net })),
      ...mayNets.map((net, i) => ({ label: `${i + 1} may`, net })),
    ];
  })(),
  gender: [
    { label: "Mujer", value: 303, percentage: 60.8 },
    { label: "Hombre", value: 131, percentage: 26.3 },
    { label: "Desconocido", value: 64, percentage: 12.9 },
  ],
  age: [
    { label: "25-34", value: 142, percentage: 28.5 },
    { label: "35-44", value: 129, percentage: 25.9 },
    { label: "45-54", value: 88, percentage: 17.7 },
    { label: "18-24", value: 72, percentage: 14.5 },
    { label: "55+", value: 67, percentage: 13.4 },
  ],
  countries: [
    { label: "Chile", value: 412, percentage: 82.7 },
    { label: "Argentina", value: 31, percentage: 6.2 },
    { label: "Perú", value: 24, percentage: 4.8 },
    { label: "España", value: 18, percentage: 3.6 },
  ],
  cities: [
    { label: "La Serena, Coquimbo Region", value: 329, percentage: 66.13 },
    { label: "Coquimbo, Coquimbo Region", value: 91, percentage: 18.24 },
    { label: "Santiago, Santiago Metropolitan Region", value: 29, percentage: 5.81 },
    { label: "Ovalle, Coquimbo Region", value: 8, percentage: 1.6 },
    { label: "Rinconada, Coquimbo Region", value: 7, percentage: 1.4 },
  ],
  ageGender: [
    { label: "Mujer 25-34", value: 96, percentage: 19.3 },
    { label: "Mujer 35-44", value: 82, percentage: 16.5 },
    { label: "Hombre 25-34", value: 46, percentage: 9.2 },
    { label: "Mujer 45-54", value: 41, percentage: 8.2 },
    { label: "Desconocido", value: 64, percentage: 12.9 },
  ],
  topContent: [
    {
      id: "ig-1",
      type: "Post",
      title: "El 2 de abril conmemoramos el Día de la Concienciación sobre el Autismo...",
      date: "7 abr 2026",
      time: "11:34",
      reach: 553,
      views: 1140,
      interactions: 48,
      likes: 23,
      comments: 1,
      saves: 1,
      shares: 2,
      engagementRate: 8.68,
      status: "Nuevo",
    },
    {
      id: "ig-2",
      type: "Carrusel",
      title: "En Mi Mundo en Movimiento, el juego tiene un propósito...",
      date: "21 abr 2026",
      time: "15:47",
      reach: 245,
      views: 473,
      interactions: 7,
      likes: 5,
      comments: 0,
      saves: 0,
      shares: 0,
      engagementRate: 2.86,
      status: "Nuevo",
    },
    {
      id: "ig-3",
      type: "Post",
      title: "El bienestar abre la puerta al aprendizaje...",
      date: "5 may 2026",
      time: "10:02",
      reach: 71,
      views: 123,
      interactions: 4,
      likes: 3,
      comments: 0,
      saves: 0,
      shares: 1,
      engagementRate: 5.63,
      status: "Nuevo",
    },
    {
      id: "ig-4",
      type: "Post",
      title: "¿Sabías que la estructura es clave en el desarrollo de niños con TEA?...",
      date: "30 abr 2026",
      time: "10:05",
      reach: 62,
      views: 109,
      interactions: 2,
      likes: 2,
      comments: 0,
      saves: 0,
      shares: 0,
      engagementRate: 3.23,
      status: "Nuevo",
    },
    {
      id: "ig-5",
      type: "Post",
      title: "En Mi Mundo en Movimiento entendemos que no todos aprenden de la misma forma...",
      date: "28 abr 2026",
      time: "14:06",
      reach: 130,
      views: 241,
      interactions: 1,
      likes: 1,
      comments: 0,
      saves: 0,
      shares: 0,
      engagementRate: 0.77,
      status: "Nuevo",
    },
  ],
  hashtags: [
    {
      id: "hash-1",
      hashtag: "#autismo",
      posts: 3,
      reach: 726,
      interactions: 54,
      engagementRate: 7.44,
    },
    {
      id: "hash-2",
      hashtag: "#educacioninclusiva",
      posts: 2,
      reach: 375,
      interactions: 18,
      engagementRate: 4.8,
    },
    {
      id: "hash-3",
      hashtag: "#terapiaocupacional",
      posts: 2,
      reach: 307,
      interactions: 11,
      engagementRate: 3.58,
    },
    {
      id: "hash-4",
      hashtag: "#infancia",
      posts: 1,
      reach: 194,
      interactions: 8,
      engagementRate: 4.12,
    },
  ],
};
